import { JourneyChanges, LifecycleGlobal } from '@/services/lifecycleAPI';

export type PilotDraft = {
  cohortStartOn: string;
  zipPrefixes: string;
  allowlist: string;
};

export const pilotDraftFromGlobal = (global: LifecycleGlobal): PilotDraft => ({
  cohortStartOn: global.cohort_start_on ?? '',
  zipPrefixes: global.cohort_zip_prefixes ?? '',
  allowlist: global.recipient_allowlist.join(', '),
});

export type BudgetDraft = { dollars: string; segmentCost: string };

export const budgetDraftFromGlobal = (
  global: LifecycleGlobal,
): BudgetDraft => ({
  dollars:
    global.monthly_sms_budget_cents === null
      ? ''
      : String(global.monthly_sms_budget_cents / 100),
  segmentCost:
    global.sms_segment_cost_cents === null
      ? ''
      : String(global.sms_segment_cost_cents),
});

export const TAX_GUIDE_MAX_LENGTH = 255;

export const taxGuideDraftFromGlobal = (global: LifecycleGlobal): string =>
  global.tax_guide_url ?? '';

// Same rules as graphy (update_journey_settings_service.rb): https only, at
// most 255 characters, blank clears it.
export const buildTaxGuideChanges = (
  initial: string,
  draft: string,
): ChangesResult => {
  const value = draft.trim();
  if (value === initial.trim()) return { ok: true, changes: {} };
  if (!value) return { ok: true, changes: { tax_guide_url: null } };
  if (!value.startsWith('https://') || value === 'https://') {
    return { ok: false, error: 'Tax guide link must start with https://' };
  }
  if (value.length > TAX_GUIDE_MAX_LENGTH) {
    return {
      ok: false,
      error: `Tax guide link must be at most ${TAX_GUIDE_MAX_LENGTH} characters`,
    };
  }
  return { ok: true, changes: { tax_guide_url: value } };
};

export const parseAllowlist = (value: string): number[] | null => {
  const tokens = value.split(/[\s,]+/).filter(Boolean);
  if (tokens.some((token) => !/^\d+$/.test(token))) return null;
  return tokens.map(Number);
};

export type ChangesResult =
  | { ok: true; changes: JourneyChanges }
  | { ok: false; error: string };

export const buildPilotChanges = (
  initial: PilotDraft,
  draft: PilotDraft,
): ChangesResult => {
  const changes: JourneyChanges = {};
  if (draft.cohortStartOn.trim() !== initial.cohortStartOn.trim()) {
    changes.cohort_start_on = draft.cohortStartOn.trim() || null;
  }
  if (draft.zipPrefixes.trim() !== initial.zipPrefixes.trim()) {
    changes.cohort_zip_prefixes = draft.zipPrefixes.trim() || null;
  }
  if (draft.allowlist.trim() !== initial.allowlist.trim()) {
    const ids = parseAllowlist(draft.allowlist);
    if (ids === null) {
      return {
        ok: false,
        error: 'Allowlist must be worker ids separated by commas.',
      };
    }
    changes.recipient_allowlist = ids;
  }
  return { ok: true, changes };
};

const numberOrRaw = (value: string): number | string => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : value;
};

// graphy validates both (0 to 999.999 cents for the segment cost) and its
// message is shown as is when a value is refused.
export const buildBudgetChanges = (
  initial: BudgetDraft,
  draft: BudgetDraft,
): ChangesResult => {
  const changes: JourneyChanges = {};
  const dollars = draft.dollars.trim();
  if (dollars !== initial.dollars.trim()) {
    const parsed = numberOrRaw(dollars);
    changes.monthly_sms_budget_cents = !dollars
      ? null
      : typeof parsed === 'number'
        ? Math.round(parsed * 100)
        : parsed;
  }
  const cost = draft.segmentCost.trim();
  if (cost !== initial.segmentCost.trim()) {
    changes.sms_segment_cost_cents = cost ? numberOrRaw(cost) : null;
  }
  return { ok: true, changes };
};
